import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { SubscriptionService } from '@app/+authenticated/+reports/shared/subscriptions/subscription.service';
import { EmployeeModel } from '@app/reducers/orm/employee/employee.model';
import { getActiveEmployees } from '@app/reducers/orm/employee/employee.selectors';
import { EmployeeService } from '@app/reducers/orm/employee/employee.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { selectAuthUserId } from '@reducers/auth/auth.selectors';
import { AppState } from '@reducers/interfaces';
import { TooltipDirective } from '@sb/tooltip';
import { ButtonComponent } from '@sb/ui/button';
import { CardComponent } from '@sb/ui/card';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { PlanQuotaModel, QuotaType } from '@shiftbase-com/ts-shared/models';
import { EMPTY } from 'rxjs';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';

import { QuotaCardRuleComponent } from '../quota-card-rule/quota-card-rule.component';

@Component({
  selector: 'app-downgrade-employees',
  imports: [
    TranslatePipe,
    RouterModule,
    TooltipDirective,
    ButtonComponent,
    CheckboxComponent,
    QuotaCardRuleComponent,
    CardComponent,
  ],
  templateUrl: './downgrade-employees.component.html',
})
export class DowngradeEmployeesComponent {
  #store = inject<Store<AppState>>(Store);
  #employeeService = inject(EmployeeService);
  #subscriptionService = inject(SubscriptionService);
  #destroyRef = inject(DestroyRef);

  subscriptionPlanQuotas = input.required<Partial<Record<QuotaType, PlanQuotaModel>>>();

  planName = input<string>();

  // Emitted when the account fits the seat limit and the user continues; the wizard finalizes the
  // switch to Free from here (this is the last step of the Free downgrade flow).
  next = output<void>();

  // Emitted to return to the previous step (locations) so the user can keep adjusting departments/teams.
  back = output<void>();

  quotaType = QuotaType;

  #activeEmployees = this.#store.selectSignal(getActiveEmployees);

  #authenticatedUserId = this.#store.selectSignal(selectAuthUserId);

  employees = computed(() =>
    this.#activeEmployees().filter((employee): employee is EmployeeModel & { id: string } => !!employee.id),
  );

  // Employees the user has unchecked — the ones that will be deactivated on Continue. Empty by default,
  // so every row starts checked (all employees kept) and the user opts individuals out from there.
  #deselectedIds = signal<ReadonlySet<string>>(new Set());

  isProcessing = signal(false);

  #seatsQuota = computed<PlanQuotaModel | undefined>(() => this.subscriptionPlanQuotas()[QuotaType.SEATS]);

  // The number of employees the plan allows to stay active; surfaced in the step copy.
  seatsLimit = computed(() => this.#seatsQuota()?.limit);

  // Employees still checked — the ones that will stay active, i.e. the projected seat usage.
  selectedCount = computed(() => this.employees().filter((employee) => this.isSelected(employee.id)).length);

  // Feed the seat quota card the live selection: its usage becomes the number of employees that will
  // remain active, so the count at the top updates as rows are checked/unchecked.
  projectedPlanQuotas = computed<Partial<Record<QuotaType, PlanQuotaModel>>>(() => {
    const planQuotas = this.subscriptionPlanQuotas();
    const seatsQuota = planQuotas[QuotaType.SEATS];

    if (!seatsQuota) {
      return planQuotas;
    }

    return { ...planQuotas, [QuotaType.SEATS]: { ...seatsQuota, usage: this.selectedCount() } };
  });

  overLimitBy = computed(() => {
    const seatsQuota = this.#seatsQuota();

    if (!seatsQuota) {
      return 0;
    }

    return Math.max(0, this.selectedCount() - seatsQuota.limit);
  });

  isOverLimit = computed(() => this.overLimitBy() > 0);

  // Continue only once the kept employees fit within the seat limit (and no deactivation is running).
  canContinue = computed(() => !this.isOverLimit() && !this.isProcessing());

  isSelected(employeeId: string): boolean {
    return !this.#deselectedIds().has(employeeId);
  }

  // The signed-in user can't deactivate their own account, so their row stays checked and disabled.
  isSelf(employeeId: string): boolean {
    return employeeId === this.#authenticatedUserId();
  }

  toggleSelection(employeeId: string): void {
    if (this.isSelf(employeeId)) {
      return;
    }

    this.#deselectedIds.update((deselected) => {
      const updated = new Set(deselected);

      if (updated.has(employeeId)) {
        updated.delete(employeeId);
      } else {
        updated.add(employeeId);
      }

      return updated;
    });
  }

  // Deactivate the unchecked employees, then refetch so the backend seat usage matches what the
  // switch-to-Free request will be validated against, and only then advance to finalize the switch.
  continue(): void {
    if (!this.canContinue()) {
      return;
    }

    const employeeIdsToDeactivate = [...this.#deselectedIds()];

    if (employeeIdsToDeactivate.length === 0) {
      this.next.emit();
      return;
    }

    this.isProcessing.set(true);

    this.#employeeService
      .removeMany(employeeIdsToDeactivate)
      .pipe(
        switchMap(() => this.#subscriptionService.getSubscription()),
        take(1),
        // A failed deactivation surfaces its own toast via the API response; stay on this step so the
        // user can retry, rather than advancing to finalize the switch with employees still active.
        catchError(() => EMPTY),
        finalize(() => this.isProcessing.set(false)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.next.emit());
  }
}
